import {
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import SDatePicker from 'components/SDatePicker';
import {
  constructDynamicInterval,
  DYNAMIC_RANGE_INTERVALS,
  DYNAMIC_RANGE_MODE_DEF_BY_TYPE,
  formatDynamicRangePreview,
  formatIntervalUnit,
} from 'app-components/utils/date';
import type { DynamicRange, DynamicRangeIntervalTimestep, GeneratedReportRanges } from 'app-components/utils/date';
import { dateSelectors } from 'app-components/flux/date';
import type { DateTime } from 'luxon';
import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useAppSelector } from 'hooks/redux';
import useQDateRangePickerStyles from './styles';

export type DynamicRangeType = 'since' | 'last' | 'recent';

export const reportRangeTypeToDynamicRangeType = (reportRange: GeneratedReportRanges): DynamicRangeType | undefined => {
  switch (reportRange) {
    case 'CUSTOM_LAST':
      return 'last';
    case 'CUSTOM_SINCE':
      return 'since';
    case 'CUSTOM_RECENT':
      return 'recent';
    default:
      return undefined;
  }
};

export const dynamicRangeTypeToReportRangeType = (dynamicRangeType: DynamicRangeType): DynamicRange['type'] => {
  switch (dynamicRangeType) {
    case 'last':
      return 'CUSTOM_LAST';
    case 'since':
      return 'CUSTOM_SINCE';
    case 'recent':
      return 'CUSTOM_RECENT';
  }
};

const useStyles = makeStyles()((theme) => ({
  dynamicViewOptionDateLabel: {
    width: '100%',
    height: 'auto',
    '&.MuiChip-root': {
      backgroundColor: theme.palette.primary.light,
      borderRadius: theme.spacing(1),
    },
    '& .MuiChip-label': {
      width: '100%',
      textAlign: 'center',
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      ...theme.typography.captionRegular,
      color: theme.palette.primary.dark,
    },
  },
}));

type OptionDateLabelProps = {
  startDate: DateTime;
  endDate: DateTime;
  endsToday?: boolean;
};

const dateFormat = 'LLL dd, yyyy';

const OptionDateLabel = ({ startDate, endDate, endsToday }: OptionDateLabelProps) => {
  const { classes } = useStyles();

  return (
    <Chip
      label={formatDynamicRangePreview(startDate, endDate, { endsToday })}
      className={classes.dynamicViewOptionDateLabel}
    />
  );
};

type SinceOptionProps = {
  sinceDate: DateTime | null;
  onSinceDateChange: (date: DateTime | null) => void;
  today: DateTime;
};

const SinceOption = React.memo(function SinceOption({ sinceDate, onSinceDateChange, today }: SinceOptionProps) {
  const isInvalidDate = !sinceDate || !sinceDate.isValid;
  return (
    <Stack direction='column' gap={1} sx={{ mt: 1 }}>
      <SDatePicker
        // @ts-expect-error FIXME TS2322
        value={sinceDate}
        onChange={(date: DateTime | null) => onSinceDateChange(date)}
        textFieldProps={{ label: 'Starting from' }}
        maxDate={today}
        inputFormat={dateFormat}
        error={isInvalidDate}
        helperText={isInvalidDate ? 'Input error: start date is not valid' : undefined}
      />
      {sinceDate?.isValid && <OptionDateLabel startDate={sinceDate} endDate={today} endsToday />}
    </Stack>
  );
});

type CountIntervalOptionProps = {
  count: number;
  interval: DynamicRangeIntervalTimestep;
  onCountChange: (count: number) => void;
  onIntervalChange: (interval: DynamicRangeIntervalTimestep) => void;
  startDate: DateTime;
  endDate: DateTime;
  endsToday?: boolean;
  minDate?: DateTime | null;
};

const CountIntervalOption = ({
  count,
  interval,
  onCountChange,
  onIntervalChange,
  startDate,
  endDate,
  endsToday,
  minDate,
}: CountIntervalOptionProps) => {
  const isError = !!minDate && startDate < minDate;
  const errorText = isError ? `Date must be on or after ${minDate.toFormat('D')}` : undefined;

  return (
    <Stack direction='column' gap={1} sx={{ mt: 1 }}>
      <Stack direction='row' gap={1} alignItems='center'>
        <TextField
          type='number'
          size='small'
          variant='filled'
          hiddenLabel
          error={isError}
          value={count}
          onChange={(e) => onCountChange(Math.max(1, parseInt(e.target.value, 10) || 1))}
          slotProps={{
            htmlInput: {
              min: 1,
            },
          }}
          sx={{
            width: 88,
          }}
        />
        <TextField
          select
          size='small'
          variant='filled'
          hiddenLabel
          error={isError}
          value={interval}
          onChange={(e) => onIntervalChange(e.target.value as DynamicRangeIntervalTimestep)}
          sx={{ flex: 1 }}
        >
          {DYNAMIC_RANGE_INTERVALS.map((value) => (
            <MenuItem key={value} value={value}>
              {formatIntervalUnit(value, count)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {errorText && <FormHelperText error>{errorText}</FormHelperText>}
      <OptionDateLabel startDate={startDate} endDate={endDate} endsToday={endsToday} />
    </Stack>
  );
};

const OPTION_CONFIG: Array<{ label: string; value: DynamicRangeType; caption: string }> = (
  ['since', 'last', 'recent'] as const
).map((value) => {
  const { label, caption } = DYNAMIC_RANGE_MODE_DEF_BY_TYPE[dynamicRangeTypeToReportRangeType(value)];
  return { label, value, caption };
});

export type DynamicViewProps = {
  onDateRangeChange?: (start: DateTime, end: DateTime, interval?: DynamicRangeIntervalTimestep, count?: number) => void;
  onSelectionChange?: (sel: DynamicRangeType) => void;
  dynamicSelection: DynamicRangeType;
  startDate?: DateTime;
  dateRangeInterval?: DynamicRangeIntervalTimestep;
  dateRangeIntervalCount?: number;
  minDate?: DateTime | null;
};

// this is only for client-side date range preview within the picker
function computeDateRange(
  sel: DynamicRangeType,
  sDate: DateTime,
  lCount: number,
  lInterval: DynamicRangeIntervalTimestep,
  rCount: number,
  rInterval: DynamicRangeIntervalTimestep,
  now: DateTime,
): { start: DateTime; end: DateTime } {
  const dynamicRange: DynamicRange =
    sel === 'since'
      ? { type: 'CUSTOM_SINCE', startDate: sDate }
      : sel === 'last'
        ? { type: 'CUSTOM_LAST', interval: lInterval, count: lCount }
        : { type: 'CUSTOM_RECENT', interval: rInterval, count: rCount };

  const interval = constructDynamicInterval(dynamicRange, now);
  // When the interval is invalid (e.g. a future since-date makes end < start, so Luxon returns
  // null for .start/.end), fall back to the raw inputs so verifyRange can detect start > end.
  return { start: interval.start ?? sDate, end: interval.end ?? now };
}

function DynamicView(props: DynamicViewProps) {
  const {
    onDateRangeChange,
    onSelectionChange,
    dynamicSelection,
    startDate,
    dateRangeInterval,
    dateRangeIntervalCount,
    minDate,
  } = props;
  const { classes: pickerClasses } = useQDateRangePickerStyles();
  const today = useAppSelector(dateSelectors.getCurrentDate);
  const [selected, setSelected] = useState<DynamicRangeType>(dynamicSelection ?? 'since');

  // default value: first day of the previous month; only relevant for 'since'
  const [sinceDate, setSinceDate] = useState<DateTime | null>(startDate ?? today.minus({ months: 1 }).set({ day: 1 }));

  const [lastCount, setLastCount] = useState((dynamicSelection === 'last' ? dateRangeIntervalCount : undefined) ?? 1);
  const [lastInterval, setLastInterval] = useState<DynamicRangeIntervalTimestep>(
    (dynamicSelection === 'last' ? (dateRangeInterval as DynamicRangeIntervalTimestep) : undefined) ?? 'WEEK',
  );

  const [recentCount, setRecentCount] = useState(
    (dynamicSelection === 'recent' ? dateRangeIntervalCount : undefined) ?? 2,
  );
  const [recentInterval, setRecentInterval] = useState<DynamicRangeIntervalTimestep>(
    (dynamicSelection === 'recent' ? (dateRangeInterval as DynamicRangeIntervalTimestep) : undefined) ?? 'MONTH',
  );

  const sinceDateFallback = sinceDate ?? today;
  const { start: lastStart, end: lastEnd } = computeDateRange(
    'last',
    sinceDateFallback,
    lastCount,
    lastInterval,
    recentCount,
    recentInterval,
    today,
  );
  const { start: recentStart, end: recentEnd } = computeDateRange(
    'recent',
    sinceDateFallback,
    lastCount,
    lastInterval,
    recentCount,
    recentInterval,
    today,
  );

  const notify = (
    sel: DynamicRangeType,
    sDate: DateTime | null,
    lCount: number,
    lInterval: DynamicRangeIntervalTimestep,
    rCount: number,
    rInterval: DynamicRangeIntervalTimestep,
  ) => {
    if (!onDateRangeChange) return;
    if (sel === 'since' && !sDate?.isValid) {
      // Pass the null/invalid start so verifyRange can detect it and disable Apply
      onDateRangeChange(sDate as unknown as DateTime, today, undefined, undefined);
      return;
    }
    const { start, end } = computeDateRange(sel, sDate ?? today, lCount, lInterval, rCount, rInterval, today);
    const interval = sel === 'last' ? lInterval : sel === 'recent' ? rInterval : undefined;
    const count = sel === 'last' ? lCount : sel === 'recent' ? rCount : undefined;
    onDateRangeChange(start, end, interval, count);
  };

  React.useEffect(() => {
    notify(selected, sinceDate, lastCount, lastInterval, recentCount, recentInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectedChange = (newSelected: DynamicRangeType) => {
    setSelected(newSelected);
    onSelectionChange?.(newSelected);
    notify(newSelected, sinceDate, lastCount, lastInterval, recentCount, recentInterval);
  };

  const renderOption = (value: DynamicRangeType) => {
    switch (value) {
      case 'since':
        return (
          <SinceOption
            sinceDate={sinceDate}
            today={today}
            onSinceDateChange={(date) => {
              setSinceDate(date);
              notify('since', date, lastCount, lastInterval, recentCount, recentInterval);
            }}
          />
        );
      case 'last':
        return (
          <CountIntervalOption
            count={lastCount}
            interval={lastInterval}
            onCountChange={(count) => {
              setLastCount(count);
              notify('last', sinceDate, count, lastInterval, recentCount, recentInterval);
            }}
            onIntervalChange={(interval) => {
              setLastInterval(interval);
              notify('last', sinceDate, lastCount, interval, recentCount, recentInterval);
            }}
            startDate={lastStart}
            endDate={lastEnd}
            minDate={minDate}
          />
        );
      case 'recent':
        return (
          <CountIntervalOption
            count={recentCount}
            interval={recentInterval}
            onCountChange={(count) => {
              setRecentCount(count);
              notify('recent', sinceDate, lastCount, lastInterval, count, recentInterval);
            }}
            onIntervalChange={(interval) => {
              setRecentInterval(interval);
              notify('recent', sinceDate, lastCount, lastInterval, recentCount, interval);
            }}
            startDate={recentStart}
            endDate={recentEnd}
            endsToday
            minDate={minDate}
          />
        );
    }
  };

  return (
    <Box
      className={pickerClasses.dynamicView}
      sx={{ padding: 3, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 300 }}
    >
      <FormControl>
        <FormLabel sx={{ mb: 2 }}>
          <Typography variant='paragraphRegular' color='textSecondary'>
            Auto-updating range
          </Typography>
        </FormLabel>
        <RadioGroup
          value={selected}
          onChange={(e) => handleSelectedChange(e.target.value as DynamicRangeType)}
          name='radio-buttons-group'
        >
          <Stack direction='column' gap={2} divider={<Divider />}>
            {OPTION_CONFIG.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={
                  <Box sx={{ pt: 1, ml: 1 }}>
                    <Typography
                      variant='paragraphRegular'
                      color={option.value === selected ? 'primary' : 'textPrimary'}
                    >
                      {option.label}
                    </Typography>
                    <Typography variant='captionRegular' color='textSecondary'>
                      {option.caption}
                    </Typography>
                    {selected === option.value && renderOption(option.value)}
                  </Box>
                }
                sx={{ alignItems: 'flex-start' }}
              />
            ))}
          </Stack>
        </RadioGroup>
      </FormControl>
    </Box>
  );
}

export default DynamicView;
